/**
 * package: com.inq.aria
 * object: ModalWindow
 * @description ModalWindow object contains two main functions; buildModal() and removeModal()
 * The modal window built here is only meaningful to visually disabled users and
 * importantly, must not block any operation of normal users. > adds double complexity.
 * This object is not mean to be in any hierarchy and created as a simple object.
 */
com.inq.aria.ModalWindow = {};

/**
 * Indicate if modal window is on
 * @type {boolean}
 */
com.inq.aria.ModalWindow.isOn = false;

/**
 * List of selectors for interactive elements
 * @type {string[]}
 */
com.inq.aria.ModalWindow.innteractiveSelectors = ["iframe", "a", "textarea", "button", "select", "[tabindex]"];

/**
 * List of selectors for elements which should have "aria-hidden"
 * @type {string[]}
 */
com.inq.aria.ModalWindow.blockSelectors = ["iframe", "div"];

/**
 * List of HTML elements which we add 'aria-hidden = true'.
 * @type {HTMLElement[]}
 */
com.inq.aria.ModalWindow.ariaDivsHidden = [];

/**
 * List of HTML elements for use in Email Modal.
 * @type {HTMLElement[]}
 */
com.inq.aria.ModalWindow.ariaDivsChat = [];

/**
 * List of HTML elements which don't start with
 * 'tc' and 'inq' and don't have 'tabindex = -1'.
 * @type {HTMLElement[]}
 */
com.inq.aria.ModalWindow.ariaTabObjs = [];

/**
 * List of objects {element: HTMLElement, tabindex: string}.
 * Here we save <div> and <a> with they tabindex for restore it after chat closed.
 * @type {{HTMLElement,string}[]}
 */
com.inq.aria.ModalWindow.ariaElementsWithTabIndex = [];

/**
 * Builds modal window using "aria-hidden" and "tabindex" attributes for element of not an out chat,
 * to hide them for navigation.
 *
 * @param {HTMLElement} docBody - window object
 */
com.inq.aria.ModalWindow.buildModal = function (docBody) {
	if (docBody) {
		com.inq.aria.ModalWindow._processInteractiveElements(docBody);
		com.inq.aria.ModalWindow._processBlockElements(docBody);
		com.inq.aria.ModalWindow.isOn = true;
	}
};

/**
 * Adds elements to 'ariaElementsWithTabIndex' or 'ariaTabObjs' base on 'tabindex' value
 * and sets '-1' as value for this element if it is needed
 *
 * @private
 * @param {HTMLElement} docBody
 */
com.inq.aria.ModalWindow._processInteractiveElements = function (docBody) {
	var element;
	var elements = docBody.querySelectorAll(com.inq.aria.ModalWindow.innteractiveSelectors.toString());
	for (var i = 0; i < elements.length; i++) {
		element = elements[i];
		if (!com.inq.aria.ModalWindow.isChatElement(element)) {
			var tabindex = element.getAttribute("tabindex");
			if (tabindex !== "-1") {
				if (tabindex) {
					com.inq.aria.ModalWindow.ariaElementsWithTabIndex.push({
						element: element,
						tabindex: tabindex
					});
				} else {
					com.inq.aria.ModalWindow.ariaTabObjs.push(element);
				}
				element.setAttribute("tabindex", "-1");
			}
		}
	}
};

/**
 * Process passed elements and add this to 'ariaDivsHidden' and sets 'true' for "aria-hidden" attribute if it is needed
 *
 * @private
 * @param {HTMLElement} docBody
 */
com.inq.aria.ModalWindow._processBlockElements = function (docBody) {
	var element;
	var elements = docBody.querySelectorAll(com.inq.aria.ModalWindow.blockSelectors.toString());
	for (var i = 0; i < elements.length; i++) {
		element = elements[i];
		if (com.inq.aria.ModalWindow.isChatElement(element)) {
			if (element.tagName.toLowerCase() === "div") {
				com.inq.aria.ModalWindow.ariaDivsChat.push(element);
			}
		} else {
			if (!element.hasAttribute("aria-hidden")) {
				com.inq.aria.ModalWindow.ariaDivsHidden.push(element);
				element.setAttribute("aria-hidden", "true");
			}
		}
	}
};

/**
 * Return true if an element is not of the chat UI element
 *
 * @param element - HTMlElement
 * @returns {boolean}
 */
com.inq.aria.ModalWindow.isChatElement = function (element) {
	return element.id !== "inqTitleBar" && (element.id.startsWith("tc") || element.id.startsWith("inq") || element.id.startsWith("nuanMessaging"));
};

/**
 * com.inq.flash.client.chatskins.SkinControl.buildModalWindow
 *
 * @public
 * @static
 */
com.inq.aria.ModalWindow.buildModalWindow = function () {
	try {
		var docBody = top.document.body;
		com.inq.aria.ModalWindow.buildModal(docBody);
		if (com.inq.flash.client.chatskins.EmailMgr.emailCanvas
			&& com.inq.flash.client.chatskins.EmailMgr.emailCanvas.getVisible()
		) {
			com.inq.flash.client.chatskins.EmailMgr.modalEmailCanvas();
		}
	} catch (ex) {
		haxe.Log.trace(
			"Failed to build ModalWindow",
			{
				fileName : "ModalWindow.js",
				lineNumber : 137,
				className : "com.inq.aria.ModalWindow",
				methodName : "buildModalWindow"
			});
	}
};

/**
 * Hides or makes enabled modal window based on the arrays which keep elements for this.
 *
 * @param {boolean} toShow - if true then modal window should be restore if not then modal window should be hidden
 */
com.inq.aria.ModalWindow.hideModalWindow = function (toShow) {
	var i, length, element, item;

	for (i = 0, length = com.inq.aria.ModalWindow.ariaDivsHidden.length; i < length; i++) {
		element = com.inq.aria.ModalWindow.ariaDivsHidden[i];
		element.setAttribute("aria-hidden", toShow.toString());
	}
	for (i = 0, length = com.inq.aria.ModalWindow.ariaTabObjs.length; i < length; i++) {
		element = com.inq.aria.ModalWindow.ariaTabObjs[i];
		if (toShow) {
			element.setAttribute("tabindex", "-1");
		} else {
			element.removeAttribute("tabindex");
		}
	}
	for (i = 0, length = com.inq.aria.ModalWindow.ariaElementsWithTabIndex.length; i < length; i++) {
		item = com.inq.aria.ModalWindow.ariaElementsWithTabIndex[i];
		if (toShow) {
			item.element.setAttribute("tabindex", "-1");
		} else {
			item.element.setAttribute("tabindex", item.tabindex);
		}
	}
};

/**
 * Removes modal window and customer page become accessible.
 */
com.inq.aria.ModalWindow.removeModalWindow = function () {
	if (!com.inq.aria.ModalWindow.isOn) {
		return;
	}

	var i, length;

	for (i = 0, length = com.inq.aria.ModalWindow.ariaTabObjs.length; i < length; i++) {
		com.inq.aria.ModalWindow.ariaTabObjs[i].removeAttribute("tabindex");
	}
	for (i = 0, length = com.inq.aria.ModalWindow.ariaDivsHidden.length; i < length; i++) {
		com.inq.aria.ModalWindow.ariaDivsHidden[i].removeAttribute("aria-hidden");
	}
	for (i = 0, length = com.inq.aria.ModalWindow.ariaElementsWithTabIndex.length; i < length; i++) {
		var item = com.inq.aria.ModalWindow.ariaElementsWithTabIndex[i];
		item.element.setAttribute("tabindex", item.tabindex);
	}

	com.inq.aria.ModalWindow.cleanUp();
};

/**
 * @description this takes care of adding, setting focus when user tabbing through the elements.
 * First and last element gets injected to the email canvas div as the first and last element.
 * when first element gets the tab focus listener sets focus on close email button.
 */
com.inq.aria.ModalWindow.setCircularModalWindow = function (div, firstEl, lastEl) {
	try {
        // This code cannot support mobile since there is no focus event with VO or TalkBack
        if (com.inq.utils.Capabilities.isMobile()) return false;

        // Remove all possible dirty situation
        com.inq.aria.ModalWindow.removeCircularModalWindow();

        com.inq.aria.ModalWindow.firstEl = firstEl;
        com.inq.aria.ModalWindow.lastEl = lastEl;

        // I cannot make circular tab order without first, last, and div.
        if (!div || !com.inq.aria.ModalWindow.firstEl || !com.inq.aria.ModalWindow.lastEl) return false;

        com.inq.aria.ModalWindow.frontBlock = document.createElement('div');
        com.inq.aria.ModalWindow.backBlock = document.createElement('div');
        com.inq.aria.ModalWindow.frontBlock.setAttribute("tabIndex", "0");
		com.inq.aria.ModalWindow.frontBlock.style.position = "absolute";
		com.inq.aria.ModalWindow.frontBlock.style.top = 0;
        com.inq.aria.ModalWindow.backBlock.setAttribute("tabIndex", "0");
		com.inq.aria.ModalWindow.backBlock.style.position = "absolute";
		com.inq.aria.ModalWindow.backBlock.style.top = 0;
        com.inq.aria.ModalWindow.frontBlock.onfocus = firstListener;
        com.inq.aria.ModalWindow.backBlock.onfocus = lastListener;

        div.insertBefore(com.inq.aria.ModalWindow.frontBlock, div.firstChild);
        div.appendChild(com.inq.aria.ModalWindow.backBlock);
	}
	catch (err) {
		return false;
	}
	return true;

	/* ------------------------ Local function section ---------------------- */
    /**
     * Local event handler listen blockLast element and set focus to the first element
     * @param evt  - event object
     */
    function lastListener(evt) {
        if (com.inq.aria.ModalWindow.firstEl && com.inq.aria.ModalWindow.firstEl.focus) {
            com.inq.aria.ModalWindow.firstEl.focus();
        }
    }

    /**
     * Local event handler listen blockFirst element and set focus to the last element
     * @param evt  - event object
     */
    function firstListener(evt) {
        if (com.inq.aria.ModalWindow.lastEl && com.inq.aria.ModalWindow.lastEl.focus) {
            com.inq.aria.ModalWindow.lastEl.focus();
        }
    }
};

/**
 * Remove Circular Modal Window added by com.inq.aria.ModalWindow.setCircularModalWindow
 */
com.inq.aria.ModalWindow.removeCircularModalWindow = function () {
    com.inq.aria.ModalWindow.firstEl = null;
    com.inq.aria.ModalWindow.lastEl = null;
    if (com.inq.aria.ModalWindow.frontBlock) {
        com.inq.aria.ModalWindow.frontBlock.parentElement.removeChild(com.inq.aria.ModalWindow.frontBlock);
        com.inq.aria.ModalWindow.frontBlock = null;
    }
    if (com.inq.aria.ModalWindow.backBlock) {
        com.inq.aria.ModalWindow.backBlock.parentElement.removeChild(com.inq.aria.ModalWindow.backBlock);
        com.inq.aria.ModalWindow.backBlock = null;
	}
};

/**
 * com.inq.flash.client.chatskins.EmailMgr.modalEmailCanvas
 *    Turn Email Canvas as modal window so Screen Reader focus stay in Email Canvas only.
 * com.inq.flash.client.chatskins.EmailMgr.modalEmailCanvas
 *
 * @public
 * @static
 * @return {boolean}   true if no exception.
 */
com.inq.aria.ModalWindow.modalEmailCanvas = function () {
	var divs, inputs, textareas, i, j, k;
	//RTDEV-24028: For embedded chats, proceed and make "email canvas" a modal.
	if (com.inq.aria.ModalWindow.isOn === false && !com.inq.flash.client.control.FlashPeer.isEmbeddedThemeValid()) {
		return false;
	}

	try {
		// only if a chat is on client document and ariaDivsHidden is null (to prevent this module is executed twice)
		if (com.inq.aria.ModalWindow.ariaDivsChat && com.inq.flash.client.chatskins.EmailMgr.ariaDivsHidden == null) {
			divs = com.inq.aria.ModalWindow.ariaDivsChat;
			com.inq.flash.client.chatskins.EmailMgr.ariaDivsHidden = [];
			com.inq.flash.client.chatskins.EmailMgr.ariaInputsHidden = [];
			com.inq.flash.client.chatskins.EmailMgr.ariaTextAreasHidden = [];
			for (i = 0; i < divs.length; i++) {
				if (typeof divs[i] !== "undefined"
					&& !(divs[i].id === "tcChat_emailInput"
						 || divs[i].id === "tcChat_btnSendEmail"
						 || divs[i].id === "tcChat_btnCancelEmail"
						 || divs[i].id === "tcChat_btnCloseEmail"
						 || divs[i].id === "tcChat_emailFormInput"
						 || divs[i].id === "tcChat_email"
						 || divs[i].id === "tcChat_Skin"
						 || divs[i].id === "tcChat_tc-aria-readme"
						 || divs[i].id === "tcChat_EmailTitle"
						 || divs[i].id === "tcChat_SuccessSend"
						 || divs[i].id === "tcChat_FailedSend"
						 || divs[i].id === "tcChat_EmailDescription"
						 || divs[i].id === "tcChat_enterEmail"
						 || divs[i].id === "nuanMessagingFrame"
					) &&
					divs[i].getAttribute("aria-hidden") != "true"
				) {

					divs[i].setAttribute("aria-hidden", true);
					com.inq.flash.client.chatskins.EmailMgr.ariaDivsHidden.push(divs[i])
				}
				else if (typeof divs[i] !== "undefined" && divs[i].id === "tcChat_Skin") {

					// exclude <input> elements from tab-order for email form
					inputs = divs[i].getElementsByTagName("input");
					for (j = 0; j < inputs.length; j++) {
						if (typeof inputs[j] !== "undefined"
							&& inputs[j].id != "tcChat_btnSendEmail_img"
							&& inputs[j].id != "tcChat_btnCancelEmail_img"
							&& inputs[j].id != "tcChat_btnCloseEmail_img"
							&& inputs[j].getAttribute("tabindex") != "-1") {
							inputs[j].setAttribute("tabindex", "-1");
							com.inq.flash.client.chatskins.EmailMgr.ariaInputsHidden.push(inputs[j])
						}
					}

					// exclude <textarea> elements from tab-order
					textareas = divs[i].getElementsByTagName("textarea");
					for (k = 0; k < textareas.length; k++) {
						if (typeof textareas[k] !== "undefined" && textareas[k].id != "tcChat_emailInput_input"
							&& textareas[k].getAttribute("tabindex") != "-1") {
							textareas[k].setAttribute("tabindex", "-1");
							com.inq.flash.client.chatskins.EmailMgr.ariaTextAreasHidden.push(textareas[k])
						}
					}
				}
			}
		}
        if (!com.inq.utils.Capabilities.isMobile()) {
            try {
                com.inq.aria.ModalWindow.setCircularModalWindow(
                    com.inq.flash.client.chatskins.EmailMgr.emailCanvas._div,
                    Application.application.getTextInput("emailInput")._input,
                    Application.application.getButton("btnCloseEmail")._img
                );
            } catch (errCircular) {
				/* Not able to set since some element doesn't exist */
            }
		}
	} catch (errEmlModal) {
		return false;
	}
	return true;
};

/**
 * com.inq.flash.client.chatskins.EmailMgr.unModalEmailCanvas
 *    Remove modal window on Email Canvas.
 *    each attribute change is in try catch block so the process can move on next item.
 *
 * @public
 * @static
 */
com.inq.aria.ModalWindow.unModalEmailCanvas = function () {

	if (com.inq.flash.client.chatskins.EmailMgr.ariaDivsHidden !== null) {
		var divs = com.inq.flash.client.chatskins.EmailMgr.ariaDivsHidden;
		for (var i = 0; i < divs.length; i++) {
			try { divs[i].setAttribute("aria-hidden", "");}
			catch (e1) { }
		}
		com.inq.flash.client.chatskins.EmailMgr.ariaDivsHidden = null;

		if (com.inq.flash.client.chatskins.EmailMgr.ariaInputsHidden) {
			var inputs = com.inq.flash.client.chatskins.EmailMgr.ariaInputsHidden;
			for (var j = 0; j < inputs.length; j++) {
				try { inputs[j].removeAttribute("tabindex");}
				catch (e2) { }
			}
		}
		com.inq.flash.client.chatskins.EmailMgr.ariaInputsHidden = null;

		if (com.inq.flash.client.chatskins.EmailMgr.ariaTextAreasHidden) {
			var textareas = com.inq.flash.client.chatskins.EmailMgr.ariaTextAreasHidden;
			for (var k = 0; k < textareas.length; k++) {
				try { textareas[k].removeAttribute("tabindex");}
				catch (e3) { }
			}
		}
		com.inq.flash.client.chatskins.EmailMgr.ariaTextAreasHidden = null;

		if (com.inq.aria.ModalWindow.isCircularTabbingEnabled) {
			com.inq.aria.ModalWindow.setCircularModalWindow();
		}
	}
};

/**
 * Applies the circle of tabbing to the application container element #tcChat_Skin.
 * @see this.setCircularModalWindow
 * @returns {boolean}
 */
com.inq.aria.ModalWindow.setCircularApplication = function() {
    /** @type {HTMLElement} */
    var container = Application.application.getPrimaryObject();
    /** @type {HTMLElement[]} */
    var tabbingElements = com.inq.aria.ModalWindow.getTabbingElementsList(container);
    /** @type {HTMLElement} */
    var firstEl = tabbingElements[0];
    /** @type {HTMLElement} */
    var lastEl = tabbingElements[tabbingElements.length - 1];
    return com.inq.aria.ModalWindow.setCircularModalWindow(container, firstEl, lastEl);
};

/**
 * Selects the active control elements which can get a focus using the TAB button of a keyboard (be able to tabulate).
 *
 * This method uses the "HTMLElement.querySelectorAll"
 *   It's supporting should be implemented in old browsers via Polifill.js
 *   @see LandingFramework.jsp in Rulesgen module
 *
 * @param {HTMLElement} parent
 * @returns {HTMLElement[]}
 */
com.inq.aria.ModalWindow.getTabbingElementsList = function(parent) {
	/** @type {HTMLElement[]} */
	var list = [];
	/** @type {NodeList} */
	var tabbingElements;
	/** @type {string} */
	var selectors = "input, textarea, select, button, a, [tabindex]"; // selectors of elements which able to tabulate

	if (parent.querySelectorAll) {
        tabbingElements = parent.querySelectorAll(selectors);
        for (var j = 0; j < tabbingElements.length; j++) {
            if (tabbingElements[j].offsetHeight != 0
                && (!tabbingElements[j].hasAttribute("tabindex") || tabbingElements[j].getAttribute("tabindex") != "-1")) {
                list.push(tabbingElements[j]);
            }
        }
	}
	return list;
};


com.inq.aria.ModalWindow.cleanUp = function () {
	com.inq.aria.ModalWindow.isOn = false;
	com.inq.flash.client.chatskins.EmailMgr.ariaDivsHidden = null;
	com.inq.flash.client.chatskins.EmailMgr.ariaInputsHidden = null;
	com.inq.flash.client.chatskins.EmailMgr.ariaTextAreasHidden = null;
	com.inq.aria.ModalWindow.ariaDivsHidden.length = 0;
	com.inq.aria.ModalWindow.ariaDivsChat.length = 0;
	com.inq.aria.ModalWindow.ariaTabObjs.length = 0;
	com.inq.aria.ModalWindow.ariaElementsWithTabIndex.length = 0;
};
